import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Minus, Star } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { api } from '../services/api';

export default function ItemModal({ item, onClose, storeOpen = true, ordersPaused = false }) {
  const { addToCart } = useApp();
  const [quantity, setQuantity] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [errors, setErrors] = useState({});
  const [restaurant, setRestaurant] = useState(null);
  const optionRefs = useRef({});

   // Get options from optionSets (not deprecated options field)
  const itemOptions = item.options || [];

  console.log("ITEM OPTIONS IN MODAL:", itemOptions);

  useEffect(() => {
    api.getRestaurant().then(setRestaurant).catch(console.error);
  }, []);

  useEffect(() => {
  setSelectedOptions(prev => {
    const updated = { ...prev };

    itemOptions.forEach(opt => {
      if (!opt.dependsOn) return;

      const selected = updated[opt.dependsOn.optionId];

      const valid =
        selected &&
        (Array.isArray(selected)
          ? selected.some(c => c.id === opt.dependsOn.choiceId)
          : selected.id === opt.dependsOn.choiceId);

      if (!valid) {
        delete updated[opt.id];
      }
    });

    return updated;
  });
}, [itemOptions]); // ✅ FIXED

useEffect(() => {
  setSelectedOptions(prev => {
    const updated = { ...prev };

    itemOptions.forEach(opt => {
      const selected = updated[opt.id];

      // 🔥 FIX: if option is NOT multi-select but stored as array → fix it
      if (!opt.multiSelect && Array.isArray(selected)) {
        updated[opt.id] = selected[0]; // keep only first
      }
    });

    return updated;
  });
}, [itemOptions]);
  
  const visibleOptions = itemOptions.filter(option => {
  if (!option.dependsOn) return true;

  const selected = selectedOptions[option.dependsOn.optionId];
  if (!selected) return false;

  // single select
  if (!Array.isArray(selected)) {
    return selected.id === option.dependsOn.choiceId;
  }

  // multi-select
  return selected.some(c => c.id === option.dependsOn.choiceId);
});
  
  const defaultImage = restaurant?.defaultItemImage || 'https://static.prod-images.emergentagent.com/jobs/7c911eb4-04a0-4f34-93d4-347dbebef30b/images/359eb04caa4922f3628d2ff4ac19a1d709065db487ebeb72617559b2c5e4fa02.png';

  const handleSingleSelect = (optionId, choice) => {
    setSelectedOptions(prev => ({ ...prev, [optionId]: choice }));
    setErrors(prev => ({ ...prev, [optionId]: null }));
  };

  const handleMultiSelect = (optionId, choice) => {
    setSelectedOptions(prev => {
      const current = prev[optionId] || [];
      const exists = current.find(c => c.id === choice.id);
      if (exists) {
        return { ...prev, [optionId]: current.filter(c => c.id !== choice.id) };
      }
      return { ...prev, [optionId]: [...current, choice] };
    });
  };

  const isMultiSelected = (optionId, choiceId) => {
    const current = selectedOptions[optionId] || [];
    return current.some(c => c.id === choiceId);
  };

  const getOptionsPrice = () => {
    return Object.values(selectedOptions).reduce((sum, choice) => {
      if (Array.isArray(choice)) {
        return sum + choice.reduce((s, c) => s + (c.price || 0), 0);
      }
      return sum + (choice?.price || 0);
    }, 0);
  };

  const totalPrice = (item.price + getOptionsPrice()) * quantity;

  const handleAddToCart = () => {
    if (!storeOpen) {
      alert(ordersPaused
        ? 'Sorry, due to high demand we are not accepting orders currently. Please check back soon.'
        : 'Sorry, the store is currently closed.');
      return;
    }
    
    // Validate required options
    const newErrors = {};
    let hasError = false;
    itemOptions.forEach(opt => {
      if (opt.required && !selectedOptions[opt.id]) {
        newErrors[opt.id] = `Please select a ${opt.name}`;
        hasError = true;
      }
    });
    if (hasError) {

      setErrors(newErrors);

      const firstInvalidOptionId = Object.keys(newErrors)[0];

      const target =
        optionRefs.current[firstInvalidOptionId];

      if (target) {
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }

      return;
    }
    addToCart(item, quantity, selectedOptions);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white w-full sm:max-w-lg sm:mx-4 rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[90vh] flex flex-col overflow-hidden animate-slide-up">
        {/* Item Image */}
        <div className="relative h-56 flex-shrink-0">
          <img
            src={item.image || defaultImage}
            alt={item.name}
            className="w-full h-full object-cover"
            style={!item.image ? { opacity: 0.4 } : {}}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 bg-white rounded-full flex items-center justify-center shadow-md hover:bg-gray-100 transition-colors duration-200"
          >
            <X className="w-5 h-5 text-gray-700" />
          </button>
          {item.badge && (
            <span className="absolute top-4 left-4 bg-amber-600 text-white text-xs font-bold px-3 py-1 rounded-full">
              {item.badge}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          <div className="flex items-start gap-2 mb-1">
            <h2 className="text-xl font-bold text-gray-900 flex-1">{item.name}</h2>
            {item.popular && (
              <span className="flex items-center gap-0.5 text-xs text-amber-600 font-semibold mt-0.5">
                <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />
                Popular
              </span>
            )}
          </div>
          <p className="text-gray-500 text-sm leading-relaxed mb-4">{item.description}</p>
          <p className="text-lg font-bold text-gray-900 mb-5">£{item.price.toFixed(2)}</p>

          {/* Options */}
          {visibleOptions.length > 0 && visibleOptions.map(option => (
              <div
                  key={option.id}
                  className="mb-5"
                >
              <div
                  ref={(el) => {
                    optionRefs.current[option.id] = el;
                  }}
                  className="flex items-center justify-between mb-3"
                >
                <h4 className="font-semibold text-gray-900 text-sm">{option.name}</h4>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  option.required
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {option.required ? 'Required' : 'Optional'}
                </span>
              </div>
              {errors[option.id] && (
                <p className="text-red-500 text-xs mb-2">{errors[option.id]}</p>
              )}
              <div className="space-y-2">
                {option.choices.map(choice => (
                  <div
                    key={choice.id}
                    onClick={() => option.multiSelect
                      ? handleMultiSelect(option.id, choice)
                      : handleSingleSelect(option.id, choice)
                    }
                    className={`flex items-center justify-between p-3 rounded-xl border-2 cursor-pointer transition-all duration-150 ${
                      option.multiSelect
                        ? isMultiSelected(option.id, choice.id)
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                        : selectedOptions[option.id]?.id === choice.id
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        option.multiSelect
                          ? isMultiSelected(option.id, choice.id)
                            ? 'border-amber-500 bg-amber-500'
                            : 'border-gray-300'
                          : selectedOptions[option.id]?.id === choice.id
                            ? 'border-amber-500 bg-amber-500'
                            : 'border-gray-300'
                      }`}>
                        {(option.multiSelect
                          ? isMultiSelected(option.id, choice.id)
                          : selectedOptions[option.id]?.id === choice.id) && (
                          <div className="w-2.5 h-2.5 bg-white rounded-full" />
                        )}
                      </div>
                      <span className="text-sm font-medium text-gray-800">{choice.name}</span>
                    </div>
                    {choice.price > 0 && (
                      <span className="text-sm font-semibold text-gray-700">+£{choice.price.toFixed(2)}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 bg-white">
          <div className="flex items-center gap-4 mb-3">
            <div className="flex items-center gap-2 bg-gray-100 rounded-xl">
              <button
                onClick={() => setQuantity(q => Math.max(1, q - 1))}
                className="w-10 h-10 flex items-center justify-center text-gray-700 hover:text-gray-900 rounded-xl hover:bg-gray-200 transition-colors duration-150"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-8 text-center font-bold text-gray-900">{quantity}</span>
              <button
                onClick={() => setQuantity(q => q + 1)}
                className="w-10 h-10 flex items-center justify-center text-gray-700 hover:text-gray-900 rounded-xl hover:bg-gray-200 transition-colors duration-150"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={handleAddToCart}
              disabled={!storeOpen}
              className={`flex-1 py-3 rounded-xl font-semibold text-base transition-colors duration-200 flex items-center justify-between px-5 ${
                storeOpen 
                  ? 'bg-gray-900 text-white hover:bg-amber-700' 
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <span>{storeOpen ? 'Add to basket' : (ordersPaused ? 'Ordering paused' : 'Store Closed')}</span>
              <span>£{totalPrice.toFixed(2)}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
